// Chat 2.0 — bubbles, 8-bit emotes, images, and the local-first message store.
// Built to the burn-after-delivery protocol (msg / msg_ack / sync / redeliver /
// room_burned) with graceful fallback to legacy 'dm' servers: every frame the
// server doesn't speak is simply never seen, and REST send/backlog still work.
import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import htm from 'htm';
import { drawSprite, petSeedFor, PALETTES, SPRITE_W, SPRITE_H } from './pet.js';
import { TicketsSection, SendTicketForm } from './social.js';

const html = htm.bind(h);

const IMG_RE = /\.(png|jpe?g|gif|webp)$/i;

// deterministic sender color — the deep stop of their buddy's palette
export function nameColor(username) {
  return PALETTES[petSeedFor(username) % PALETTES.length][1];
}

function Pet({ username, cell = 3 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) drawSprite(ref.current, petSeedFor(username), { cell });
  }, [username, cell]);
  return html`<canvas ref=${ref} class="pixel" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px" />`;
}

// ---- 8-bit emotes: hand-set 12x12 grids, 2-3 colors each ----
// '.' empty · '1'/'2'/'3' index into c. Reused by ship reactions later — keep the shape.
export const EMOTES = {
  joy: {
    c: ['#f5c542', '#1a1826', '#66b8ff'],
    g: ['....1111....', '..11111111..', '.1111111111.', '.1221111221.', '311111111113', '311222222113',
        '.1122222211.', '.1112222111.', '..11111111..', '...111111...', '............', '............'],
  },
  cry: {
    c: ['#f5c542', '#1a1826', '#66b8ff'],
    g: ['....1111....', '..11111111..', '.1111111111.', '.1221111221.', '.1331111331.', '.1131111311.',
        '.1111111111.', '.1112222111.', '.1121111211.', '..11111111..', '...111111...', '............'],
  },
  heart: {
    c: ['#e8556d', '#ff9db0'],
    g: ['............', '.1211..1111.', '121111111111', '111111111111', '111111111111', '.1111111111.',
        '..11111111..', '...111111...', '....1111....', '.....11.....', '............', '............'],
  },
  'pixel-heart-broken': {
    c: ['#e8556d', '#ff9db0'],
    g: ['............', '.1211..1111.', '12111.111111', '1111..111111', '11111.111111', '.1111..1111.',
        '..111.1111..', '...11..111..', '....1.11....', '.....1......', '............', '............'],
  },
  fire: {
    c: ['#f08a3c', '#f5c542', '#e8556d'],
    g: ['.....1......', '.....11.....', '....111..3..', '....1111.3..', '...111111...', '..1111111...',
        '..11221111..', '.1112221111.', '.1122221111.', '.1122221111.', '..11222111..', '...111111...'],
  },
  skull: {
    c: ['#f4f1ea', '#1a1826'],
    g: ['...111111...', '..11111111..', '.1111111111.', '.1221111221.', '.1221111221.', '.1111221111.',
        '..11111111..', '...111111...', '...121121...', '...111111...', '............', '............'],
  },
  coffee: {
    c: ['#f4f1ea', '#8a5a33', '#9b9ba3'],
    g: ['...3...3....', '....3...3...', '...3...3....', '............', '.11111111...', '.1222222111.',
        '.12222221.1.', '.12222221.1.', '.1222222111.', '.11111111...', '..111111....', '............'],
  },
  keyboard: {
    c: ['#3b3654', '#f4f1ea'],
    g: ['............', '............', '............', '111111111111', '121212121211', '111111111111',
        '121212121211', '111111111111', '112222222211', '111111111111', '............', '............'],
  },
  bug: {
    c: ['#35d07f', '#1a1826'],
    g: ['..2......2..', '...2....2...', '....1111....', '...111111...', '2..112211..2', '.2.112211.2.',
        '...112211...', '.2.112211.2.', '2..112211..2', '...111111...', '....1111....', '............'],
  },
  ship: {
    c: ['#f4f1ea', '#8a5a33', '#66b8ff'],
    g: ['......2.....', '....112.....', '...1112.....', '..11112.....', '.111112.....', '..11112.....',
        '......2.....', '.222222222..', '..2222222...', '3.3.3.3.3.3.', '.3.3.3.3.3.3', '............'],
  },
  seed: {
    c: ['#8a5a33', '#35d07f'],
    g: ['............', '.....2......', '....22......', '.....2......', '....111.....', '...11111....',
        '...11111....', '...11111....', '....111.....', '............', '............', '............'],
  },
  sparkle: {
    c: ['#f5c542', '#f4f1ea'],
    g: ['............', '.....1......', '.....1...2..', '....111.....', '....111.....', '.111111111..',
        '....111.....', '....111.....', '..2..1......', '.....1......', '............', '............'],
  },
  thumbsup: {
    c: ['#f5c542', '#3b3654'],
    g: ['............', '.....11.....', '.....11.....', '....111.....', '.2211111111.', '.2211111111.',
        '.2211111111.', '.2211111111.', '.2211111111.', '...1111111..', '............', '............'],
  },
  thinking: {
    c: ['#f5c542', '#1a1826', '#7f77dd'],
    g: ['.........33.', '........3.3.', '...111....3.', '..11111..3..', '.1111111.3..', '.1211211....',
        '.1111111.3..', '.1111111....', '.1122111....', '..11111.....', '...111......', '............'],
  },
  sweat: {
    c: ['#f5c542', '#1a1826', '#66b8ff'],
    g: ['............', '..........3.', '...1111...33', '..111111..33', '.11111111.3.', '.12211221...',
        '.11111111...', '.11111111...', '.11222211...', '..111111....', '...1111.....', '............'],
  },
  party: {
    c: ['#e8556d', '#f5c542', '#35d07f'],
    g: ['.....2...3..', '..3....2....', '.......3..2.', '....2....3..', '......11....', '.....111....',
        '....1111....', '...12221....', '..111111....', '.111111.....', '111111......', '11111.......'],
  },
  zzz: {
    c: ['#7f77dd', '#cecbf6'],
    g: ['............', '......11111.', '.........1..', '........1...', '......11111.', '...1111.....',
        '.....1......', '....1.......', '...1111.....', '.222........', '..2.........', '.222........'],
  },
  eyes: {
    c: ['#f4f1ea', '#1a1826'],
    g: ['............', '............', '..11....11..', '.1111..1111.', '.1122..1122.', '.1122..1122.',
        '.1111..1111.', '..11....11..', '............', '............', '............', '............'],
  },
  rocket: {
    c: ['#f4f1ea', '#e8556d', '#f5c542'],
    g: ['.....2......', '....222.....', '....111.....', '....121.....', '....121.....', '....111.....',
        '...21112....', '..2211122...', '..2211122...', '.....3......', '....333.....', '.....3......'],
  },
  star: {
    c: ['#f5c542', '#f4f1ea'],
    g: ['.....11.....', '.....11.....', '....1111....', '111111111111', '.1211111111.', '..11111111..',
        '...111111...', '...111111...', '..111..111..', '.11......11.', '............', '............'],
  },
  rain: {
    c: ['#9b9ba3', '#66b8ff'],
    g: ['....1111....', '..11111111..', '.1111111111.', '.1111111111.', '..2..2..2...', '.2..2..2..2.',
        '..2..2..2...', '.2..2..2..2.', '............', '............', '............', '............'],
  },
  sun: {
    c: ['#f5c542', '#f08a3c'],
    g: ['.....11.....', '.2...11...2.', '..2.1111.2..', '...111111...', '2.11111111.2', '..11111111..',
        '..11111111..', '2.11111111.2', '...111111...', '..2.1111.2..', '.2...11...2.', '.....11.....'],
  },
  cat: {
    c: ['#9b9ba3', '#1a1826', '#f2a7bd'],
    g: ['.1........1.', '.11......11.', '.111....111.', '.1111111111.', '.1111111111.', '.1221111221.',
        '.1111331111.', '.1112112111.', '.1111111111.', '..11111111..', '............', '............'],
  },
  ghost: {
    c: ['#f4f1ea', '#1a1826'],
    g: ['....1111....', '..11111111..', '.1111111111.', '.1221111221.', '.1221111221.', '.1111111111.',
        '.1111221111.', '.1111111111.', '.1111111111.', '.11.11.11.1.', '............', '............'],
  },
};

export function drawEmote(canvas, code, cell = 2) {
  const e = EMOTES[code];
  if (!e || !canvas) return;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  e.g.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] === '.') continue;
      ctx.fillStyle = e.c[+row[x] - 1] ?? e.c[0];
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  });
}

export function EmoteSprite({ code, cell = 2 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) drawEmote(ref.current, code, cell);
  }, [code, cell]);
  return html`<canvas ref=${ref} class="pixel emote" title=${`:${code}:`} width=${12 * cell} height=${12 * cell}
    style="width:${12 * cell}px;height:${12 * cell}px" />`;
}

const TOKEN_RE = /:([a-z0-9_-]{2,24}):/g;

function soloEmote(body) {
  const m = String(body ?? '').trim().match(/^:([a-z0-9_-]{2,24}):$/);
  return m && EMOTES[m[1]] ? m[1] : null;
}

// text with :emote: tokens → mixed spans + canvas sprites
function renderBody(body) {
  const parts = [];
  let i = 0;
  let mt;
  TOKEN_RE.lastIndex = 0;
  while ((mt = TOKEN_RE.exec(body))) {
    if (!EMOTES[mt[1]]) continue;
    if (mt.index > i) parts.push(body.slice(i, mt.index));
    parts.push(html`<${EmoteSprite} code=${mt[1]} cell=${2} />`);
    i = mt.index + mt[0].length;
  }
  if (i < body.length) parts.push(body.slice(i));
  return parts;
}

// ---- IndexedDB: per-user message log + notification ring ----
// db per username: two folks on one browser must never share (or answer redelivers from) each other's log

const dbs = new Map();
function openStore(user) {
  if (!user) return Promise.reject(new Error('no user'));
  if (!dbs.has(user)) {
    const p = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') return reject(new Error('no idb'));
      const req = indexedDB.open(`vb-chat-${user}`, 1);
      req.onupgradeneeded = () => {
        const d = req.result;
        if (!d.objectStoreNames.contains('msgs')) d.createObjectStore('msgs', { keyPath: ['conv_id', 'seq'] });
        if (!d.objectStoreNames.contains('notifs')) d.createObjectStore('notifs', { keyPath: 'id', autoIncrement: true });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    p.catch(() => dbs.delete(user)); // a failed open must not poison retries
    dbs.set(user, p);
  }
  return dbs.get(user);
}

async function idbOp(user, store, mode, run) {
  const d = await openStore(user);
  return new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const out = run(t.objectStore(store));
    t.oncomplete = () => resolve(out?.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const convRange = (convId) => IDBKeyRange.bound([Number(convId), -Infinity], [Number(convId), Infinity]);

function putMsg(user, rec) {
  rec = { ...rec, conv_id: Number(rec.conv_id), seq: Number(rec.seq) };
  if (!Number.isFinite(rec.conv_id) || !Number.isFinite(rec.seq)) return Promise.resolve();
  return idbOp(user, 'msgs', 'readwrite', (s) => s.put(rec));
}

const loadConv = async (user, convId) => (await idbOp(user, 'msgs', 'readonly', (s) => s.getAll(convRange(convId)))) ?? [];
const getMsgRec = (user, convId, seq) => idbOp(user, 'msgs', 'readonly', (s) => s.get([Number(convId), Number(seq)]));
const wipeConv = (user, convId) => idbOp(user, 'msgs', 'readwrite', (s) => s.delete(convRange(convId)));

// only protocol-assigned seqs count toward sync — legacy row ids would lie to the server
async function lastSeqMap(user) {
  const all = (await idbOp(user, 'msgs', 'readonly', (s) => s.getAll())) ?? [];
  const last = {};
  for (const r of all) if (r.proto) last[r.conv_id] = Math.max(last[r.conv_id] ?? 0, r.seq);
  return last;
}

// ---- protocol hooks (called from the ws handler in main.js) ----

const normFrom = (f) => (typeof f === 'string' ? f : (f?.username ?? '?'));

export async function receiveMsgFrame(user, m, ws) {
  try {
    await putMsg(user, {
      conv_id: m.conv_id, seq: m.seq, proto: 1,
      from: normFrom(m.from), ts: m.ts, kind: m.kind ?? 'text', body: m.body ?? '',
      hash: m.hash, relayed: !!m.relayed, via_agent: !!m.via_agent,
    });
    // ack only after the message truly lives here — the ack is what lets the server burn it
    if (ws?.readyState === 1) ws.send(JSON.stringify({ t: 'msg_ack', conv_id: m.conv_id, seq: m.seq }));
  } catch {
    // no local store: withhold the ack, the server's 7-day TTL keeps the message safe
  }
}

export function receiveLegacyDm(user, m) {
  const msg = m.msg;
  if (!msg?.id) return;
  if (msg.seq != null) return; // dual-frame server: receiveMsgFrame owns local storage
  putMsg(user, {
    conv_id: m.conv_id, seq: msg.id, proto: 0,
    from: normFrom(msg.from), ts: msg.ts, kind: 'text', body: msg.body ?? '',
    attachment_url: msg.attachment_url ?? null, attachment_name: msg.attachment_name ?? null,
    via_agent: !!msg.via_agent,
  }).catch(() => {});
}

export async function sendChatSync(user, ws) {
  let last = {};
  try {
    last = await lastSeqMap(user);
  } catch {}
  if (ws?.readyState === 1) ws.send(JSON.stringify({ t: 'sync', last }));
}

export async function answerRedeliver(user, m, ws) {
  try {
    for (const seq of m.seqs ?? []) {
      const rec = await getMsgRec(user, m.conv_id, seq);
      if (rec && ws?.readyState === 1)
        ws.send(JSON.stringify({
          t: 'redeliver', conv_id: m.conv_id, seq: rec.seq,
          body: rec.body, kind: rec.kind ?? 'text', orig_from: rec.from, orig_ts: rec.ts,
        }));
    }
  } catch {}
}

export function wipeConversation(user, convId) {
  return wipeConv(user, Number(convId)).catch(() => {});
}

// ---- notification drawer (local-only ring: last 100 / 7 days) ----

const notifWatchers = new Set();
function onNotifChange(fn) {
  notifWatchers.add(fn);
  return () => notifWatchers.delete(fn);
}

function describeEvent(m) {
  switch (m.t) {
    case 'friend_event':
      if (m.kind === 'game_started')
        return { text: `${m.username} started ${m.game_kind} — watch live`, tab: 'play', game_id: m.game_id };
      if (m.kind === 'best_buddy') return { text: `${m.username} made you a best buddy 💛`, tab: 'buddies', username: m.username };
      if (m.kind === 'invite_grown') return { text: `${m.username} joined from your invite — you both got +5 seeds`, tab: 'garden' };
      return null;
    case 'garden_visit':
      return { text: `${m.from} ${m.action === 'water' ? 'watered your garden' : 'left a butterfly in your garden'}`, tab: 'garden' };
    case 'pet_pat':
      return { text: `${m.from} patted your buddy`, tab: 'buddies', username: m.from };
    case 'game_invite':
      return { text: `${m.game?.player_a?.username ?? 'someone'} challenges you to ${m.game?.kind ?? 'a game'}`, tab: 'play' };
    case 'friend_request':
      return { text: `${m.from} sent you a friend card`, tab: 'buddies' };
    default:
      return null;
  }
}

export async function addNotification(user, evt) {
  // agent_done fires on every finished run — a busy agent washed everything else
  // out of the drawer. hard-filtered HERE so no caller can persist it; the live
  // toast + system notification still announce the run.
  if (evt?.t === 'agent_done') return;
  const n = describeEvent(evt);
  if (!n || !user) return;
  try {
    await idbOp(user, 'notifs', 'readwrite', (s) => {
      s.put({ ...n, ts: Date.now(), read: 0 });
      const cutoff = Date.now() - 7 * 86400 * 1000;
      const req = s.getAll();
      req.onsuccess = () => {
        const all = req.result.sort((a, b) => b.ts - a.ts);
        all.forEach((r, i) => {
          if (i >= 100 || r.ts < cutoff) s.delete(r.id);
        });
      };
    });
  } catch {}
  for (const fn of notifWatchers) fn();
}

const loadNotifs = async (user) =>
  ((await idbOp(user, 'notifs', 'readonly', (s) => s.getAll())) ?? []).sort((a, b) => b.ts - a.ts);

const markNotifsRead = (user) =>
  idbOp(user, 'notifs', 'readwrite', (s) => {
    const req = s.getAll();
    req.onsuccess = () => {
      for (const n of req.result) if (!n.read) s.put({ ...n, read: 1 });
    };
  });

// rows from before the agent_done ban purge themselves on first read — the ring
// never stored an event kind, so the text prefix is the only signature they left
const notifPurged = new Set();
function purgeAgentDone(user) {
  if (notifPurged.has(user)) return Promise.resolve();
  notifPurged.add(user);
  return idbOp(user, 'notifs', 'readwrite', (s) => {
    const req = s.getAll();
    req.onsuccess = () => {
      for (const n of req.result) if (/^your agent finished/.test(n.text ?? '')) s.delete(n.id);
    };
  }).catch(() => {});
}

function agoShort(ts) {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const BELL_GRID = ['....##....', '...####...', '..######..', '..######..', '..######..', '.########.', '..........', '....##....'];

function BellIcon() {
  const rects = [];
  BELL_GRID.forEach((row, y) => {
    for (let x = 0; x < row.length; x++)
      if (row[x] === '#') rects.push(html`<rect x=${x * 2} y=${y * 2} width="2" height="2" fill="currentColor" />`);
  });
  return html`<svg width="18" height="18" viewBox="0 0 20 16" shape-rendering="crispEdges" aria-hidden="true">${rects}</svg>`;
}

export function NotificationBell({ me, onNavigate }) {
  const user = me?.username;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [earlier, setEarlier] = useState(false); // the folded already-seen shelf
  const [pos, setPos] = useState({ top: 64, right: 8 }); // viewport coords, measured on open
  const wrapRef = useRef(null);
  const drawerRef = useRef(null);
  const refresh = useCallback(() => {
    if (user) purgeAgentDone(user).then(() => loadNotifs(user)).then(setItems).catch(() => {});
  }, [user]);
  useEffect(() => {
    refresh();
    return onNotifChange(refresh);
  }, [refresh]);
  const unread = items.filter((n) => !n.read).length;
  const toggle = () => {
    const next = !open;
    if (next) {
      // the drawer portals to <body> as position:fixed — measure the statusbar
      // so it hangs just below the bar, right-aligned, in pure viewport coords
      // (z 60: statusbar-popover layer — see the scale in main.js)
      const bar = wrapRef.current?.closest('.statusbar');
      const r = (bar ?? wrapRef.current)?.getBoundingClientRect();
      if (r) setPos({ top: Math.round(r.bottom + 4), right: Math.max(6, Math.round(window.innerWidth - r.right + 8)) });
      setEarlier(false);
    }
    setOpen(next);
    if (next && user && unread > 0) {
      // keep the fresh highlight for this viewing, clear the dot for the next
      const fresh = new Set(items.filter((n) => !n.read).map((n) => n.id));
      markNotifsRead(user)
        .then(() => setItems((its) => its.map((n) => (fresh.has(n.id) ? { ...n, read: 1, fresh: 1 } : n))))
        .catch(() => {});
    }
  };
  // esc / outside tap close — the drawer lives outside the bell's dom now
  useEffect(() => {
    if (!open) return;
    const down = (e) => {
      if (drawerRef.current?.contains(e.target) || wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const key = (e) => e.key === 'Escape' && setOpen(false);
    const gone = () => setOpen(false); // fixed coords go stale on resize — just close
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', gone);
    };
  }, [open]);
  // tiering: what's new (unread this opening) stands tall; everything already
  // seen folds under 'earlier' so the drawer only ever shouts about fresh news
  const top = items.filter((n) => !n.read || n.fresh);
  const seen = items.filter((n) => n.read && !n.fresh);
  const row = (n) => html`<div class="notifrow ${!n.read || n.fresh ? 'freshn' : ''}"
    onClick=${() => {
      setOpen(false);
      onNavigate?.(n);
    }}>
    <span class="when">${agoShort(n.ts)}</span><span>${n.text}</span>
  </div>`;
  return html`<span class="bellwrap" ref=${wrapRef} onClick=${(e) => e.stopPropagation()}>
    <button class="bellbtn" title="while you were away" onClick=${toggle}>
      <${BellIcon} />
      ${unread > 0 && html`<span class="belldot"></span>`}
    </button>
    ${open &&
    createPortal(
      html`<div class="belldrawer" ref=${drawerRef}
        style="position:fixed;top:${pos.top}px;right:${pos.right}px;left:auto;z-index:60">
        ${items.length === 0 && html`<div class="notifempty">all quiet — nothing yet</div>`}
        ${items.length > 0 && top.length === 0 && html`<div class="notifempty">all caught up ✨</div>`}
        ${top.map(row)}
        ${seen.length > 0 &&
        html`<div class="notifrow dim clickable" onClick=${() => setEarlier(!earlier)}>
          <span class="when"></span><span>earlier · ${seen.length} ${earlier ? '▴' : '▾'}</span>
        </div>`}
        ${earlier && seen.map(row)}
      </div>`,
      document.body
    )}
  </span>`;
}

// ---- image attach: client-side compression, bytes go straight to storage ----

// createImageBitmap first (fast, no DOM), object-URL + <img> as the fallback —
// some webviews lack createImageBitmap or choke on exif'd jpegs it would reject
function decodeImage(file) {
  const viaImg = () =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('bad image'));
      };
      img.src = url;
    });
  if (typeof createImageBitmap !== 'function') return viaImg();
  return createImageBitmap(file).catch(viaImg);
}

async function compressImage(file) {
  const src = await decodeImage(file);
  const w = src.naturalWidth ?? src.width;
  const h = src.naturalHeight ?? src.height;
  if (!w || !h) throw new Error('empty image');
  const scale = Math.min(1, 1280 / Math.max(w, h));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  const ctx = c.getContext('2d');
  // jpeg has no alpha — opaque backing keeps transparent pngs from going black
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(src, 0, 0, c.width, c.height);
  src.close?.();
  return new Promise((resolve, reject) =>
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.8)
  );
}

// ---- composer icons: hand-set 11x11 pixel grids, drawn like the emotes ----

const ICONS = {
  smiley: {
    c: ['#f5c542', '#1a1826'],
    g: ['...11111...', '..1111111..', '.111111111.', '11221112211', '11221112211', '11111111111',
        '12111111121', '.121111121.', '..1222221..', '..1111111..', '...11111...'],
  },
  photo: {
    c: ['#f4f1ea', '#f5c542', '#35d07f'],
    g: ['11111111111', '1.........1', '1.22......1', '1.22...3..1', '1.....333.1', '1....3333.1',
        '1...33333.1', '1..333333.1', '1.3333333.1', '13333333331', '11111111111'],
  },
  ticket: {
    c: ['#f5c542', '#1a1826'],
    g: ['...........', '...........', '11111111111', '11111112111', '.111111211.', '.111111211.',
        '11111112111', '11111112111', '11111111111', '...........', '...........'],
  },
};

export function PixelIcon({ name, cell = 2 }) {
  const ic = ICONS[name];
  if (!ic) return null;
  const rects = [];
  ic.g.forEach((row, y) => {
    for (let x = 0; x < row.length; x++)
      if (row[x] !== '.')
        rects.push(html`<rect x=${x * cell} y=${y * cell} width=${cell} height=${cell} fill=${ic.c[+row[x] - 1]} />`);
  });
  const w = ic.g[0].length * cell;
  const h = ic.g.length * cell;
  return html`<svg width=${w} height=${h} viewBox="0 0 ${w} ${h}" shape-rendering="crispEdges" aria-hidden="true">${rects}</svg>`;
}

// ---- the pieces: Composer, Bubble, Thread, ChatsTab ----

export function Composer({ onSend, onPickImage, onTicket, busy }) {
  const [draft, setDraft] = useState('');
  const [picker, setPicker] = useState(false);
  const fileRef = useRef(null);
  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    setPicker(false);
    const ok = await onSend(body);
    if (!ok) setDraft((d) => d || body); // hand the words back on failure
  };
  return html`<div>
    ${picker &&
    html`<div class="emotepanel">
      ${Object.keys(EMOTES).map(
        (code) => html`<button class="emotebtn" title=${`:${code}:`}
          onClick=${() => setDraft((d) => `${d}${d && !d.endsWith(' ') ? ' ' : ''}:${code}: `)}>
          <${EmoteSprite} code=${code} cell=${2} />
        </button>`
      )}
    </div>`}
    <div class="composer">
      <input type="file" ref=${fileRef} style="display:none" accept="image/*"
        onChange=${(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onPickImage(f);
        }} />
      <button class="iconbtn" title="emotes" onClick=${() => setPicker(!picker)}>
        <${PixelIcon} name="smiley" />
      </button>
      <button class="iconbtn" title="send a picture" disabled=${busy} onClick=${() => fileRef.current?.click()}>
        ${busy ? '…' : html`<${PixelIcon} name="photo" />`}
      </button>
      ${onTicket &&
      html`<button class="iconbtn" title="send a work ticket" onClick=${onTicket}>
        <${PixelIcon} name="ticket" />
      </button>`}
      <input type="text" maxlength="2000" placeholder="message…" value=${draft}
        onInput=${(e) => setDraft(e.target.value)} onKeyDown=${(e) => e.key === 'Enter' && submit()} />
      <button onClick=${submit}>send</button>
    </div>
  </div>`;
}

function Bubble({ m, me, onProfile, onView }) {
  const own = m.from === me;
  const img =
    m.kind === 'image'
      ? (m.attachment_url ?? m.body)
      : m.attachment_url && IMG_RE.test(m.attachment_name ?? '')
        ? m.attachment_url
        : null;
  const file = !img && m.attachment_url ? m.attachment_url : null;
  const showBody = m.body && m.body !== img;
  const solo = showBody && !img && soloEmote(m.body);
  const badges = html`${m.via_agent && html`<span class="agentbadge">agent</span>`}
    ${m.relayed && html`<span class="relayed" title="recovered from a buddy's device">⇄</span>`}`;
  return html`<div class="bubblerow ${own ? 'own' : ''}">
    <div class="bubble">
      ${!own &&
      html`<div class="bwho clickable" style="color:${nameColor(m.from)}" onClick=${() => onProfile?.(m.from)}>
        ${m.from}${badges}
      </div>`}
      ${own && (m.via_agent || m.relayed) && html`<div class="bwho">${badges}</div>`}
      ${img && html`<img class="chatimg" src=${img} onClick=${() => onView?.(img)} />`}
      ${file && html`<a class="filelink" href=${file} target="_blank">📎 ${m.attachment_name}</a>`}
      ${showBody &&
      (solo
        ? html`<div class="soloemote"><${EmoteSprite} code=${solo} cell=${4} /></div>`
        : html`<div class="bbody">${renderBody(m.body)}</div>`)}
    </div>
  </div>`;
}

// one shape for every source: local record, REST backlog row, or ws frame
const normMsg = (m) => ({
  seq: Number(m.seq ?? m.id ?? 0),
  proto: m.proto ?? (m.seq != null ? 1 : 0),
  from: normFrom(m.from),
  via_agent: !!m.via_agent,
  kind: m.kind ?? 'text',
  body: m.body ?? '',
  attachment_url: m.attachment_url ?? null,
  attachment_name: m.attachment_name ?? null,
  ts: Number(m.ts ?? 0),
  relayed: !!m.relayed,
});
const msgKey = (m) => `${m.proto ? 'p' : 'l'}:${m.seq}`;

export function Thread({ convId, convName, conv, me, feedEvents, onBack, onProfile, onRead, onLeft, onToast }) {
  const [msgs, setMsgs] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [imgView, setImgView] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const feedRef = useRef(null);
  const user = me.username;
  // a just-opened DM may not be in the social list yet — missing conv means DM
  const isDm = conv ? conv.kind === 'dm' : true;

  // local-first: burned messages only exist on this device — merge, don't replace.
  // fetching also advances last_read server-side, same as before the extraction.
  const load = useCallback(async () => {
    const local = await loadConv(user, convId).catch(() => []);
    let server = [];
    try {
      const r = await fetch(`/api/conversations/${convId}/messages`);
      if (r.ok) server = (await r.json()).messages ?? [];
    } catch {}
    const have = new Set(local.map((r) => msgKey(normMsg(r))));
    for (const m of server) {
      const n = normMsg(m);
      // persist only what's new — a re-fetch must not clobber the hash the ws frame carried
      if (!have.has(msgKey(n))) putMsg(user, { ...n, conv_id: convId, hash: m.hash }).catch(() => {});
    }
    const seen = new Map();
    for (const m of [...local, ...server].map(normMsg)) seen.set(msgKey(m), m);
    setMsgs([...seen.values()].sort((a, b) => a.ts - b.ts || a.seq - b.seq));
    onRead?.(convId);
  }, [convId]);
  useEffect(() => {
    load();
  }, [convId]);

  // live updates via ws events forwarded from App ('dm' legacy · 'msg' protocol)
  useEffect(() => {
    const last = feedEvents[feedEvents.length - 1];
    if (!last || last.conv_id !== convId) return;
    const raw = last.t === 'dm' ? last.msg : last.t === 'msg' ? last : null;
    if (!raw) return;
    const n = normMsg(raw);
    setMsgs((prev) => {
      const list = prev ?? [];
      return list.some((x) => msgKey(x) === msgKey(n)) ? list : [...list, n];
    });
    // someone else's message while we're looking: refetch so the server knows we read it
    if (n.from !== user) load();
  }, [feedEvents]);

  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs]);

  const post = async (payload) => {
    setErr('');
    try {
      const r = await fetch(`/api/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        setErr((await r.json()).error ?? 'failed');
        return false;
      }
      return true;
    } catch {
      setErr('could not reach the server');
      return false;
    }
  };

  const sendText = (body) => post({ body, kind: 'text' });

  const sendImage = async (f) => {
    setErr('');
    setBusy(true);
    try {
      // each step names its own failure — a blanket catch here once blamed the
      // image for CSP-blocked storage PUTs ('could not read that image' on every send)
      let blob;
      try {
        blob = await compressImage(f);
      } catch {
        return setErr('could not read that image');
      }
      const name = (f.name || 'picture').replace(/\.[^.]*$/, '') + '.jpg';
      const meta = await fetch('/api/uploads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, size: blob.size, type: 'image/jpeg' }),
      }).catch(() => null);
      if (!meta) return setErr('could not reach the server');
      if (!meta.ok) return setErr((await meta.json().catch(() => ({}))).error ?? 'upload refused');
      const { upload_url, file_url } = await meta.json();
      // s3 rigs hand back a cross-origin storage URL — a THROWN fetch here is
      // CSP/CORS/network, never the picture's fault
      // content-type is signed into the url — pin the header rather than trusting blob.type
      const put = await fetch(upload_url, { method: 'PUT', headers: { 'content-type': 'image/jpeg' }, body: blob }).catch(() => null);
      if (!put) return setErr('upload blocked — could not reach storage');
      if (!put.ok) return setErr('upload failed');
      // body = url per the burn contract; attachment fields keep legacy servers rendering it
      await post({ body: file_url, kind: 'image', attachment_url: file_url, attachment_name: name });
    } catch {
      setErr('could not send that picture');
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!confirm('离开后聊天记录将永久消失,但你们的相遇会留在历史里')) return;
    const r = await fetch(`/api/conversations/${convId}/leave`, { method: 'POST' });
    if (r.ok) {
      await wipeConversation(user, convId);
      onLeft?.();
    } else {
      onToast?.(
        r.status === 404
          ? 'this server cannot leave rooms yet'
          : ((await r.json().catch(() => ({}))).error ?? 'could not leave')
      );
    }
  };

  return html`
    <div class="roomhead threadhead">
      <span class="clickable" onClick=${onBack}>←</span>
      <b>${convName}</b>
      ${conv?.kind === 'group' && html`<span class="mini-link leavelink" onClick=${leave}>leave</span>`}
    </div>
    <div class="burnhint">messages vanish from the server once delivered — they live on your devices</div>
    <div class="feed" ref=${feedRef}>
      ${msgs === null && html`<div class="empty">…</div>`}
      ${msgs?.length === 0 && html`<div class="empty"><div>say hi — it stays between your devices</div></div>`}
      ${msgs?.map((m) => html`<${Bubble} m=${m} me=${user} onProfile=${onProfile} onView=${setImgView} />`)}
    </div>
    ${err && html`<div class="formerr">${err}</div>`}
    ${ticketOpen &&
    isDm &&
    html`<div class="threadticket">
      <div class="dim" style="margin-bottom:6px">🎫 hand work to ${convName}</div>
      <${SendTicketForm} toUsername=${convName}
        onDone=${() => {
          setTicketOpen(false);
          onToast?.(`🎫 ticket sent to ${convName}`);
        }} />
    </div>`}
    <${Composer} onSend=${sendText} onPickImage=${sendImage} busy=${busy}
      onTicket=${isDm ? () => setTicketOpen((v) => !v) : null} />
    ${imgView &&
    html`<div class="overlay imgoverlay" style="z-index:85" onClick=${() => setImgView(null)}>
      <img src=${imgView} />
    </div>`}
  `;
}

const dateShort = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString() : '');

export function ChatsTab({ social, onOpenThread, onProfile, reloadSocial, me, onToast, ticketsVersion }) {
  const [groupMode, setGroupMode] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [picked, setPicked] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  if (!social) return html`<div class="empty">…</div>`;
  const { friends, conversations } = social;
  // ended shells (room history) ride the same list once the server keeps them
  const active = conversations.filter((c) => !c.ended_at && !c.ended);
  const past = conversations.filter((c) => c.ended_at || c.ended);

  const createGroup = async () => {
    if (!picked.length) return;
    const r = await fetch('/api/conversations/group', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: groupName || 'group', members: picked }),
    });
    if (r.ok) {
      setGroupMode(false);
      setPicked([]);
      setGroupName('');
      const { conv_id } = await r.json();
      reloadSocial();
      onOpenThread(conv_id);
    }
  };

  const addFriend = async (username) => {
    const r = await fetch('/api/friends/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    const d = await r.json().catch(() => ({}));
    onToast?.(r.ok ? `friend card sent to ${username} ♥` : (d.error ?? 'could not send'));
    if (r.ok) reloadSocial?.();
  };

  return html`<div class="chats">
    <div class="section">
      <div class="sectionhead">
        conversations
        <span class="mini-link" onClick=${() => setGroupMode(!groupMode)}>${groupMode ? 'cancel' : '+ group'}</span>
      </div>
      ${groupMode &&
      html`<div class="groupform">
        <input type="text" placeholder="group name" value=${groupName} onInput=${(e) => setGroupName(e.target.value)} />
        <div class="pickrow">
          ${friends.map(
            (f) => html`<label class="pick">
              <input
                type="checkbox"
                checked=${picked.includes(f.user.username)}
                onChange=${(e) =>
                  setPicked(
                    e.target.checked
                      ? [...picked, f.user.username]
                      : picked.filter((x) => x !== f.user.username)
                  )}
              />
              ${f.user.username}
            </label>`
          )}
        </div>
        <button class="mini" onClick=${createGroup}>create</button>
      </div>`}
      ${active.length === 0 && html`<div class="dim">no chats yet — add a buddy below or roll a table.</div>`}
      ${active.map(
        (c) => html`<div class="convrow clickable" onClick=${() => onOpenThread(c.conv_id)}>
          ${c.kind === 'dm' ? html`<${Pet} username=${c.name} cell=${3} />` : html`<span class="groupglyph">⌂</span>`}
          <div class="convtxt">
            <div class="convname">${c.name} ${c.unread && html`<span class="unreaddot"></span>`}</div>
            ${c.last && html`<div class="convlast">${c.last.body}</div>`}
          </div>
        </div>`
      )}
    </div>

    ${past.length > 0 &&
    html`<div class="section">
      <div class="sectionhead clickable" onClick=${() => setHistOpen(!histOpen)}>
        past rooms · ${past.length} <span class="dim">${histOpen ? '▴' : '▾'}</span>
      </div>
      ${histOpen &&
      past.map((c) => {
        const others = (c.members ?? []).filter((u) => u !== me.username);
        return html`<div class="convrow histrow">
          <span class="groupglyph">⌂</span>
          <div class="convtxt">
            <div class="convname">${c.name}</div>
            <div class="convlast">
              with ${others.join(', ') || '?'}
              ${(c.created_at || c.ended_at) && ` · ${dateShort(c.created_at)} – ${dateShort(c.ended_at ?? c.ended)}`}
            </div>
          </div>
          ${others.slice(0, 2).map(
            (u) => html`<button class="mini" title=${`send ${u} a friend card`}
              onClick=${(e) => {
                e.stopPropagation();
                addFriend(u);
              }}>♥ ${u}</button>`
          )}
        </div>`;
      })}
    </div>`}
    <${TicketsSection} me=${me} ticketsVersion=${ticketsVersion} />
  </div>`;
}
